import { Box, Checkbox, Stack, Typography } from '@mui/material';
import DataTable from '../../../components/DataTable/DataTable';
import { VisitaEstadoChip } from '../../Visitas/components/EstadoChip';
import { VisitaApi } from '../../Visitas/consts/types';
import { getDate } from '../../../utils/common';
import NoInfo from '../../../components/NoInfo';
import LugarOcurrenciaCell from './LugarOcurrenciaCell';
import NoTableDataImage from '../../../assets/image/empty-state-establecimientos-backoffice.png';

interface Props {
    visitas: VisitaApi[];
    totalCount: number;
    loading: boolean;
    filters: any;
    setFilters: (f: any) => void;
    onAccionClick: (event: React.MouseEvent<HTMLElement>, visita: VisitaApi) => void;
    canSelectVisits?: boolean;
    isChecked?: (id: number) => boolean;
    handleCheckPress?: (item: VisitaApi) => void;
    isAllChecked?: boolean;
    handleAllCheckPress?: (visibleVisitas: VisitaApi[]) => void;
}

const COLUMN_TITLES = [
    'ID Visita',
    'Nro ROAM',
    'Lugar de ocurrencia',
    'Estado',
    'Tipo',
    'Fecha Programada',
    'Fecha Límite',
    'Acciones'
];

const COLUMN_WIDTHS = ['5%', '10%', '23%', '14%', '14%', '11%', '11%', '5%'];
const SELECTION_COLUMN_WIDTH = '3%';

function GrillaVisitasRoam({
    visitas,
    totalCount,
    loading,
    filters,
    setFilters,
    onAccionClick,
    canSelectVisits = false,
    isChecked,
    handleCheckPress,
    isAllChecked = false,
    handleAllCheckPress
}: Props) {
    // Spread condicional (no `cond && {...}`): si se dejara el objeto false dentro del
    // array, DataTableRow igual renderiza una celda por cada entrada de headers (ver
    // DataTableCell.tsx, default case), generando una columna vacia fantasma que no
    // matchea con columnTitles/columnWidths cuando canSelectVisits es false.
    const headers = [
        ...(canSelectVisits
            ? [
                  {
                      type: 'selection',
                      centerColumn: true,
                      template: (item: VisitaApi) => (
                          <Checkbox
                              checked={isChecked?.(item.id) ?? false}
                              onClick={(event) => event.stopPropagation()}
                              onChange={() => handleCheckPress?.(item)}
                          />
                      )
                  }
              ]
            : []),
        {
            type: 'data',
            upperLabel: (item: VisitaApi) => item?.id || '-'
        },
        {
            type: 'data',
            upperLabel: (item: VisitaApi) => item?.siniestro?.nroRoam || '-'
        },
        {
            type: 'data',
            upperLabel: (item: VisitaApi) => <LugarOcurrenciaCell siniestro={item?.siniestro} />,
            align: 'left'
        },
        {
            type: 'data',
            upperLabel: (item: VisitaApi) =>
                item?.estado ? (
                    <VisitaEstadoChip estadoId={item.estado} observacion={item.observacion} />
                ) : (
                    '-'
                )
        },
        {
            type: 'data',
            upperLabel: (item: VisitaApi) => {
                if (!item?.actividades?.length) return '-';
                return item.actividades[0]?.tipoVisita?.descripcion || '-';
            },
            align: 'left'
        },
        {
            type: 'data',
            upperLabel: (item: VisitaApi) => item?.fechaProgramada ? getDate(item.fechaProgramada) : '-',
            align: 'left'
        },
        {
            type: 'data',
            upperLabel: (item: VisitaApi) => item?.fechaLimite ? getDate(item.fechaLimite) : '-',
            align: 'left'
        },
        { type: 'action', onClick: onAccionClick }
    ];

    const isEmpty = !loading && (!visitas || visitas.length === 0);

    if (isEmpty) {
        return (
            <Box
                display="flex"
                alignItems="center"
                justifyContent="center"
                flexDirection="column"
                my={2}
            >
                <Stack direction="column" justifyContent="center" alignItems="center">
                    <img src={NoTableDataImage} alt="" />
                    <Typography variant="h6" sx={{ mt: 2, fontWeight: 'bold' }}>
                        No tenés visitas ROAM asignadas.
                    </Typography>
                </Stack>
            </Box>
        );
    }

    const columnTitles = [
        ...(canSelectVisits
            ? [
                  {
                      label: '',
                      showSelectAllCheck: true,
                      checked: isAllChecked,
                      onSelectAllCheckPress: () => handleAllCheckPress?.(visitas)
                  }
              ]
            : []),
        ...COLUMN_TITLES.map((label) => ({ label }))
    ];
    const columnWidths = [
        ...(canSelectVisits ? [SELECTION_COLUMN_WIDTH] : []),
        ...COLUMN_WIDTHS
    ];

    return (
        <DataTable
            columnTitles={columnTitles}
            columnWidths={columnWidths}
            menuOpen={false}
            headers={headers}
            onSelectRow={() => {}}
            isLoading={loading}
            items={{ data: { data: visitas, count: totalCount } } as any}
            filters={filters}
            setFilters={setFilters}
            isFetching={loading}
        />
    );
}

export default GrillaVisitasRoam;
